import { Injectable } from "@angular/core";
import { AbstractControl, ValidationErrors } from "@angular/forms";
@Injectable({
  providedIn: 'root'
})
export class HelperService {
  constructor() { }

  /**
   *
   * @param {string} cpf CPF a ser validade
   * @returns {boolean} true se for válido, false caso contrário
   */
  isCPFValido = isCPFValido;

  /**
   * @param {string} cnpj CNPJ a ser validado
   * @returns {boolean} true se for válido, false caso contrário
   */
  isCnpjValido = isCnpjValido;

  /**
   *
   * @param {number} a o tamanho do arquivo, em bytes
   * @returns {string} string de bytes formatada
   */
  formatBytes(a, b = 2, k = 1024): string {
    let d = Math.floor(Math.log(a) / Math.log(k));
    return 0 == a ? "0 Bytes" : parseFloat((a / Math.pow(k, d)).toFixed(Math.max(0, b))) + " " + ["Bytes", "KB", "MB", "GB", "TB", "PB", "EB", "ZB", "YB"][d]
  }

  /**
   *
   * @param {string} cpf Uma string CPF contendo apenas números de comprimento 11
   * @returns {string} Uma string CPF com os pontos e traços correspondentes.
   */
  addCPFLashes(cpf: string | number): string {
    if (typeof cpf === "number") cpf = cpf.toString();
    if (cpf == undefined || cpf.length !== 11)
      return
    return `${cpf.substring(0, 3)}.${cpf.substring(3, 6)}.${cpf.substring(6, 9)}-${cpf.substring(9, 11)}`
  }

  /**
   * @param {any} moment String de data, data, número ou momento a ser formatada
   * @param {number} yearOffset Deslocamento opcional a ser adicionado ao ano da data formatada
   * @param {number} monthOffset Deslocamento opcional a ser adicionado ao mês da data formatada
   * @param {number} dayOffset Deslocamento opcional a ser adicionado ao dia da data formatada
   * @returns {string} Data formatada no padrão YYYY-MM-DD HH:mm:ss
   */
  formatarData(moment: any, yearOffset?: number, monthOffset?: number, dayOffset?: number): string | null {
    if (!moment) return null;

    let date: Date;

    // 1. Tratamento para objetos Moment.js do Angular Material
    if (moment && typeof moment === 'object') {
      if (typeof moment.toDate === 'function') {
        date = new Date(moment.toDate().getTime());
      } else if (moment._d) {
        date = new Date(moment._d.getTime());
      } else {
        date = new Date(moment);
      }
    }
    // 2. TRATAMENTO CRÍTICO: Strings vindas do Backend (ex: "2026-10-15" ou "2026-10-15T00:00:00...")
    else if (typeof moment === 'string') {
      // Se for string no formato YYYY-MM-DD sem fuso horário explícito ou com T00:00:00
      // O construtor new Date("2026-10-15") subtrai horas no Brasil.
      // Substituir '-' por '/' ou forçar horário local neutraliza a conversão UTC indesejada:
      const dataLimpa = moment.split('T')[0]; // Pega apenas YYYY-MM-DD
      const partes = dataLimpa.split('-');

      if (partes.length === 3) {
        // Cria a data no meio-dia local (12:00:00) para blindar contra qualquer variação de fuso
        date = new Date(Number(partes[0]), Number(partes[1]) - 1, Number(partes[2]), 12, 0, 0);
      } else {
        date = new Date(moment);
      }
    } else {
      date = new Date(moment);
    }

    if (isNaN(date.getTime())) {
      return null;
    }

    // Deslocamentos opcionais
    if (yearOffset) date.setFullYear(date.getFullYear() + yearOffset);
    if (monthOffset) date.setMonth(date.getMonth() + monthOffset);
    if (dayOffset) date.setDate(date.getDate() + dayOffset);

    const padZero = (num: number): string => num.toString().padStart(2, '0');

    const ano = date.getFullYear();
    const mes = padZero(date.getMonth() + 1);
    const dia = padZero(date.getDate());

    const horas = padZero(date.getHours());
    const minutos = padZero(date.getMinutes());
    const segundos = padZero(date.getSeconds());

    return `${ano}-${mes}-${dia} ${horas}:${minutos}:${segundos}`;
  }

  /**
   *
   * @param {AbstractControl} control Controle passado pelo material component
   * @returns {ValidationErrors | null} Erro se inválido, nulo se não houver erros.
   */
  emptyArrayValidator(control: AbstractControl): ValidationErrors | null {
    if (control.value.length === 0) return { 'empty': true };
    return null;
  }

  /**
   *
   * @param {AbstractControl} control Controle passado pelo material component
   * @returns {ValidationErrors | null} Erro se inválido, nulo se não houver erros
   */
  cpfValidator(control: AbstractControl): ValidationErrors | null {
    if (!isCPFValido(control.value)) return { 'invalidCPF': true };
    return null;
  }

  /**
   *
   * @param control Controle passado pelo material component
   * @returns Erro se inválido, nulo se não houver erros
   */
  cnpjValidator(control: AbstractControl): ValidationErrors | null {
    if (!isCnpjValido(control.value)) return { 'invalidCNPJ': true };
    return null;
  }

  /**
   *
   * @param {object} obj Um objeto a ser limpo
   * @returns o objeto reduzido, sem chaves com valores nulos
   */
  cleanEmpty(obj: object): object {
    return Object.entries(obj)
      .map(([k, v]) => [k, v && typeof v === "object" ? this.cleanEmpty(v) : v])
      .reduce((a, [k, v]) => (v == null ? a : (a[k] = v, a)), {});
  }

  /**
   *
   * @param data
   * @param dias
   * @returns
   */
  addDiasUteis(data: Date | string, dias: number): Date {
    let dataInicial = new Date(data);
    let dataFinal: Date;
    for (let i = 0; i < dias;) {
      dataFinal = new Date(dataInicial.setDate(dataInicial.getDate() + 1));
      if (dataFinal.getDay() !== 0 && dataFinal.getDay() !== 6)
        i++;
    }
    return dataFinal;
  }

  /**
   *
   * @param cnpj CNPJ a ser formatado
   * @returns CNPJ com formatação padrão
   */
  formatarCNPJ(cnpj: string): string {
    if (!cnpj) return '';

    // Remove qualquer caractere que não seja letra ou número
    const cleaned = cnpj.replace(/[^a-zA-Z0-9]/g, '').toUpperCase();

    // Se não tiver 14 caracteres ainda, retorna o que for possível formatar
    const parte1 = cleaned.slice(0, 2);
    const parte2 = cleaned.slice(2, 5);
    const parte3 = cleaned.slice(5, 8);
    const parte4 = cleaned.slice(8, 12);
    const parte5 = cleaned.slice(12, 14);

    let formatado = '';

    if (parte1) formatado += parte1;
    if (parte2) formatado += '.' + parte2;
    if (parte3) formatado += '.' + parte3;
    if (parte4) formatado += '/' + parte4;
    if (parte5) formatado += '-' + parte5;

    return formatado;
  }

  /**
   *
   * @param control Controle passado pelo material component
   * @returns Erro se inválido, nulo se não houver erros
   */
  cepValidator(control: AbstractControl): ValidationErrors | null {
    if (!this.validarCEP(control.value)) return { 'invalidCEP': true };
    return null;
  }

  validarCEP(cep: string): boolean {
    if (!cep) return false;

    // Remove tudo que não for número
    const cleaned = cep.replace(/\D/g, '');

    // CEP deve ter exatamente 8 dígitos
    if (cleaned.length !== 8) return false;

    // evita sequências iguais
    if (/^(\d)\1+$/.test(cleaned)) return false;

    return true;
  }

  formatarCEP(cep: string): string {
    if (!cep) return '';

    // Remove tudo que não for número
    const cleaned = cep.replace(/\D/g, '');

    // Pega as partes do CEP
    const parte1 = cleaned.slice(0, 5);
    const parte2 = cleaned.slice(5, 8);

    if (cleaned.length <= 5) return parte1;

    return `${parte1}-${parte2}`;
  }
}

const isCPFValido = (cpf: string | number): boolean => {
  if (typeof cpf === "number") cpf = cpf.toString()
  if (typeof cpf !== "string") return false
  cpf = cpf.replace(/[\s.-]*/igm, '')
  if (
    !cpf ||
    cpf.length != 11 ||
    cpf == "00000000000" ||
    cpf == "11111111111" ||
    cpf == "22222222222" ||
    cpf == "33333333333" ||
    cpf == "44444444444" ||
    cpf == "55555555555" ||
    cpf == "66666666666" ||
    cpf == "77777777777" ||
    cpf == "88888888888" ||
    cpf == "99999999999"
  ) {
    return false
  }
  var soma = 0
  var resto
  for (var i = 1; i <= 9; i++)
    soma = soma + parseInt(cpf.substring(i - 1, i)) * (11 - i)
  resto = (soma * 10) % 11
  if ((resto == 10) || (resto == 11)) resto = 0
  if (resto != parseInt(cpf.substring(9, 10))) return false
  soma = 0
  for (var i = 1; i <= 10; i++)
    soma = soma + parseInt(cpf.substring(i - 1, i)) * (12 - i)
  resto = (soma * 10) % 11
  if ((resto == 10) || (resto == 11)) resto = 0
  if (resto != parseInt(cpf.substring(10, 11))) return false
  return true
}

const isCnpjValido = (cnpj: string): boolean => {
  if (!cnpj) return false;

  // Remove apenas caracteres especiais (mantém letras e números)
  const cleaned = cnpj.replace(/[^a-zA-Z0-9]/g, '').toUpperCase();

  if (cleaned.length !== 14) return false;

  // Elimina sequências iguais (ex: 000..., AAA..., etc)
  if (/^([A-Z0-9])\1+$/.test(cleaned)) return false;

  // Função que converte caractere em número segundo regra do CNPJ de outubro de 2024
  // O valor de cada letra no cálculo do dígito verificador será dado pelo valor na tabela ASCII, subtraído de 48
  const charToValue = (char: string): number => {
    if (/[0-9]/.test(char)) return parseInt(char, 10);
    return char.charCodeAt(0) - 48;
  };

  const calcularDigito = (base: string, pesos: number[]): number => {
    const soma = base
      .split('')
      .reduce((acc, char, i) => acc + charToValue(char) * pesos[i], 0);

    const resto = soma % 11;
    return resto < 2 ? 0 : 11 - resto;
  };

  const base = cleaned.slice(0, 12);

  const pesosPrimeiro = [5, 4, 3, 2, 9, 8, 7, 6, 5, 4, 3, 2];
  const digito1 = calcularDigito(base, pesosPrimeiro);

  const pesosSegundo = [6, ...pesosPrimeiro];
  const digito2 = calcularDigito(base + digito1.toString(), pesosSegundo);

  return cleaned === base + digito1.toString() + digito2.toString();
}
